import { useEffect, useMemo, useRef, useState } from 'react';
import {
  cueHighlights,
  cueIsActive,
  DARK_INK,
  NowYouTry,
  usePrefersReducedMotion,
  useMountSequence,
  VizCard,
  type VizCue,
} from '../vizShared';

// PhIndicator — a universal-indicator color chart (pH 0–14) with a clearer
// emphasis on the *indicator color* than the slider-style ph_scale. A tall
// color column runs red→orange→yellow→green→blue→indigo→violet; a movable
// marker reads off the [H⁺]/[OH⁻] concentrations at that pH, and familiar
// reference substances are labeled at their position along the column with the
// nearest one spotlighted. Click a substance (or drag the marker) to explore.
//
// config: { ph?, sample?, title? }
// Resilient: every field defaults; nothing here can throw on bad input.
// Cue: cue.progress → pH = progress*14; cue.step → walks substances;
//      cue.highlight → spotlights a named substance.

export interface PhIndicatorConfig {
  /** Starting pH for the marker (0–14). */
  ph?: number;
  /** Name (or fragment) of a reference substance to spotlight on load. */
  sample?: string;
  title?: string;
}

interface Props {
  config?: PhIndicatorConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

const KW = 1e-14;

// Universal-indicator color stops (classic chart hues) sweeping the full
// red→violet rainbow across pH 0–14.
const INDICATOR_STOPS: Array<{ ph: number; color: string }> = [
  { ph: 0, color: '#C0263B' }, // red
  { ph: 2, color: '#E0563B' }, // orange-red
  { ph: 4, color: '#E8923C' }, // orange
  { ph: 5, color: '#E8C53C' }, // yellow
  { ph: 6, color: '#C7D63C' }, // yellow-green
  { ph: 7, color: '#5FB85F' }, // green
  { ph: 8, color: '#3CB89B' }, // teal
  { ph: 9, color: '#3C9BD6' }, // blue
  { ph: 11, color: '#3C5BD6' }, // indigo
  { ph: 13, color: '#6A3CC7' }, // violet
  { ph: 14, color: '#4A1C8C' }, // deep violet
];

// Common reference substances by approximate pH (the brief's set, with a few
// extras for a fuller column). The nearest one to the marker is spotlighted.
const SUBSTANCES: Array<{ ph: number; name: string; emoji: string }> = [
  { ph: 0, name: 'Battery acid', emoji: '🔋' },
  { ph: 2.2, name: 'Lemon juice', emoji: '🍋' },
  { ph: 5, name: 'Coffee', emoji: '☕' },
  { ph: 7, name: 'Pure water', emoji: '💧' },
  { ph: 9, name: 'Baking soda', emoji: '🧁' },
  { ph: 11.5, name: 'Ammonia', emoji: '🧹' },
  { ph: 12.6, name: 'Bleach', emoji: '🧽' },
];

function clampPh(p: number) {
  if (!Number.isFinite(p)) return 7;
  return Math.min(14, Math.max(0, p));
}

function lerpColor(a: string, b: string, t: number) {
  const pa = [parseInt(a.slice(1, 3), 16), parseInt(a.slice(3, 5), 16), parseInt(a.slice(5, 7), 16)];
  const pb = [parseInt(b.slice(1, 3), 16), parseInt(b.slice(3, 5), 16), parseInt(b.slice(5, 7), 16)];
  const c = pa.map((v, i) => Math.round(v + (pb[i] - v) * t));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

function colorForPh(ph: number) {
  const p = clampPh(ph);
  for (let i = 0; i < INDICATOR_STOPS.length - 1; i++) {
    const lo = INDICATOR_STOPS[i];
    const hi = INDICATOR_STOPS[i + 1];
    if (p >= lo.ph && p <= hi.ph) {
      const t = (p - lo.ph) / (hi.ph - lo.ph || 1);
      return lerpColor(lo.color, hi.color, t);
    }
  }
  return INDICATOR_STOPS[INDICATOR_STOPS.length - 1].color;
}

function closestSubstanceIdx(ph: number) {
  let best = 0;
  for (let i = 1; i < SUBSTANCES.length; i++) {
    if (Math.abs(SUBSTANCES[i].ph - ph) < Math.abs(SUBSTANCES[best].ph - ph)) best = i;
  }
  return best;
}

// Format a concentration in scientific notation for readability.
function sci(x: number) {
  if (!Number.isFinite(x) || x <= 0) return '0';
  const exp = Math.floor(Math.log10(x));
  const mant = x / 10 ** exp;
  return `${mant.toFixed(1)} × 10${supExp(exp)}`;
}
const SUPS: Record<string, string> = { '-': '⁻', '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹' };
function supExp(n: number) {
  return String(n).split('').map((ch) => SUPS[ch] ?? ch).join('');
}

// SVG geometry — a tall vertical column. pH 0 (acidic) at the BOTTOM, pH 14
// (basic) at the TOP, matching the way a chart is usually read.
const VW = 360;
const VH = 360;
const BAR_X = 26;
const BAR_W = 56;
const BAR_TOP = 18;
const BAR_BOTTOM = VH - 18;
const BAR_H = BAR_BOTTOM - BAR_TOP;

// y-pixel for a given pH (0 at bottom, 14 at top).
function yForPh(ph: number) {
  return BAR_BOTTOM - (clampPh(ph) / 14) * BAR_H;
}

export default function PhIndicator({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  // Resolve a starting pH from config.ph, or from a named sample, or default.
  const sampleIdx = useMemo(() => {
    if (!config.sample) return -1;
    const q = config.sample.toLowerCase();
    return SUBSTANCES.findIndex((s) => s.name.toLowerCase().includes(q));
  }, [config.sample]);

  const initialPh = clampPh(
    config.ph != null ? config.ph : sampleIdx >= 0 ? SUBSTANCES[sampleIdx].ph : 3,
  );

  const [ph, setPh] = useState<number>(reduced || hasCue ? initialPh : 0);
  const interactedRef = useRef(false);
  const barRef = useRef<SVGRectElement | null>(null);

  const mark = () => {
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'ph_indicator', ph });
    }
  };

  // ---- Self-demo: sweep the indicator down then up the column, then settle.
  const demo = useMountSequence(
    [
      { at: 200, run: () => setPh(0) },
      { at: 520, run: () => setPh(4) },
      { at: 840, run: () => setPh(7) },
      { at: 1160, run: () => setPh(10.5) },
      { at: 1480, run: () => setPh(14) },
      { at: 1860, run: () => setPh(initialPh) },
    ],
    { skip: reduced || hasCue, onSettle: () => setPh(initialPh) },
  );
  const cancelDemo = () => { if (demo.running) demo.cancel(); };

  // ---- Cue: progress → pH across the column, but ONLY while playing. At rest
  // (paused) keep the configured pH so the slide doesn't snap to pH 0.
  useEffect(() => {
    if (cue?.progress == null || cue.play !== true) return;
    setPh(clampPh(Math.max(0, Math.min(1, cue.progress)) * 14));
  }, [cue?.progress, cue?.play]);

  // ---- Cue: step → walk through the labeled substances.
  useEffect(() => {
    if (cue?.step == null) return;
    const idx = Math.max(0, Math.min(SUBSTANCES.length - 1, Math.round(cue.step)));
    setPh(clampPh(SUBSTANCES[idx].ph));
  }, [cue?.step]);

  // ---- Cue: highlight → spotlight a named substance.
  useEffect(() => {
    const hi = cueHighlights(cue);
    if (hi.length === 0) return;
    const s = SUBSTANCES.find((sub) => hi.some((h) => sub.name.toLowerCase().includes(h)));
    if (s) setPh(clampPh(s.ph));
  }, [cue]);

  const h = 10 ** -ph;
  const oh = KW / h;
  const poh = 14 - ph;
  const color = colorForPh(ph);
  const nearIdx = closestSubstanceIdx(ph);
  const near = SUBSTANCES[nearIdx];
  const nature = ph < 6.5 ? 'Acidic' : ph > 7.5 ? 'Basic' : 'Neutral';
  const my = yForPh(ph);

  const transition = reduced ? undefined : 'all 320ms ease-out';

  const selectSubstance = (idx: number) => {
    cancelDemo();
    setPh(clampPh(SUBSTANCES[idx].ph));
    mark();
  };

  // Map a pointer y-coordinate (in SVG user units) to a pH and apply it.
  const setPhFromClientY = (clientY: number) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || rect.height === 0) return;
    const frac = 1 - (clientY - rect.top) / rect.height; // 0 at bottom → 1 at top
    setPh(clampPh(frac * 14));
  };

  const draggingRef = useRef(false);
  const onBarPointerDown = (e: React.PointerEvent) => {
    cancelDemo();
    draggingRef.current = true;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setPhFromClientY(e.clientY);
    mark();
  };
  const onBarPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    setPhFromClientY(e.clientY);
  };
  const onBarPointerUp = () => { draggingRef.current = false; };

  const nudge = (delta: number) => { cancelDemo(); setPh((p) => clampPh(p + delta)); mark(); };

  return (
    <VizCard
      reducedMotion={reduced}
      eyebrow="Acid–base · Universal indicator"
      title={config.title ?? 'Universal indicator color chart'}
      subtitle="The indicator color at each pH, with [H⁺] / [OH⁻] and familiar substances"
      hint="Drag the marker on the color column, click a substance, or use ↑/↓ · sweeps a demo on load"
    >
      <div className="relative w-full select-none">
        <NowYouTry show={demo.done && !reduced && !hasCue} />

        <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch">
          {/* The indicator color column with substance labels + marker. */}
          <div className="sm:w-1/2">
            <svg
              viewBox={`0 0 ${VW} ${VH}`}
              className="w-full touch-none"
              role="img"
              aria-label={`Universal indicator column, marker at pH ${ph.toFixed(1)}`}
            >
              <defs>
                <linearGradient id="phIndGrad" x1="0" y1="1" x2="0" y2="0">
                  {INDICATOR_STOPS.map((s) => (
                    <stop key={s.ph} offset={`${(s.ph / 14) * 100}%`} stopColor={s.color} />
                  ))}
                </linearGradient>
              </defs>

              {/* Color column (rounded like a test tube). */}
              <rect
                ref={barRef}
                x={BAR_X}
                y={BAR_TOP}
                width={BAR_W}
                height={BAR_H}
                rx={14}
                fill="url(#phIndGrad)"
                stroke="var(--ws-border)"
                strokeWidth="1"
                style={{ cursor: 'ns-resize' }}
                onPointerDown={onBarPointerDown}
                onPointerMove={onBarPointerMove}
                onPointerUp={onBarPointerUp}
                onPointerCancel={onBarPointerUp}
              />

              {/* pH gridlines every 2 units + numeric labels just left of the bar. */}
              {Array.from({ length: 8 }, (_, k) => k * 2).map((p) => {
                const gy = yForPh(p);
                return (
                  <g key={p}>
                    <line x1={BAR_X - 6} y1={gy} x2={BAR_X} y2={gy} stroke="var(--ws-ink-4)" strokeWidth="1" />
                    <text x={BAR_X - 9} y={gy + 3.5} textAnchor="end" fontSize="11" fill="var(--ws-ink-3)">
                      {p}
                    </text>
                  </g>
                );
              })}

              {/* Acidic / Neutral / Basic zone hints down the right edge of bar. */}
              <text x={BAR_X + BAR_W + 8} y={yForPh(1.5) + 4} fontSize="9" fill="var(--ws-ink-3)">acidic</text>
              <text x={BAR_X + BAR_W + 8} y={yForPh(7) + 4} fontSize="9" fill="var(--ws-ink-3)">neutral</text>
              <text x={BAR_X + BAR_W + 8} y={yForPh(12.5) + 4} fontSize="9" fill="var(--ws-ink-3)">basic</text>

              {/* Substance labels with leader lines to their pH on the bar. */}
              {SUBSTANCES.map((s, i) => {
                const sy = yForPh(s.ph);
                const on = i === nearIdx;
                const lx = BAR_X + BAR_W + 44;
                return (
                  <g key={s.name} onClick={() => selectSubstance(i)} style={{ cursor: 'pointer' }}>
                    <line
                      x1={BAR_X + BAR_W}
                      y1={sy}
                      x2={lx}
                      y2={sy}
                      stroke={on ? 'var(--ws-accent)' : 'var(--ws-ink-4)'}
                      strokeWidth={on ? 1.5 : 1}
                      strokeDasharray={on ? undefined : '2 2'}
                    />
                    <circle cx={BAR_X + BAR_W} cy={sy} r={on ? 3.5 : 2.5} fill={on ? 'var(--ws-accent)' : 'var(--ws-ink-4)'} />
                    <text
                      x={lx + 6}
                      y={sy - 1}
                      fontSize="11"
                      fontWeight={on ? 700 : 500}
                      fill={on ? 'var(--ws-accent)' : 'var(--ws-ink-2)'}
                    >
                      {s.emoji} {s.name}
                    </text>
                    <text x={lx + 6} y={sy + 11} fontSize="9" fill="var(--ws-ink-3)">
                      pH {s.ph}
                    </text>
                  </g>
                );
              })}

              {/* The movable marker: a pointer onto the bar + a color chip. */}
              <g style={{ transition }} transform={`translate(0, ${my})`}>
                <line x1={BAR_X - 2} y1={0} x2={BAR_X + BAR_W + 2} y2={0} stroke="var(--ws-ink)" strokeWidth="2" />
                <polygon points={`${BAR_X - 14},-6 ${BAR_X - 14},6 ${BAR_X - 4},0`} fill="var(--ws-ink)" />
                <g transform={`translate(${BAR_X + BAR_W / 2}, 0)`}>
                  <circle r="13" fill={color} stroke="var(--ws-ink)" strokeWidth="1.5" style={{ transition }} />
                  <text y="3.5" textAnchor="middle" fontSize="10" fontWeight="700" fill={DARK_INK}>
                    {ph.toFixed(1)}
                  </text>
                </g>
              </g>
            </svg>
          </div>

          {/* Readouts: big indicator swatch, nature, [H⁺]/[OH⁻], nearest sample. */}
          <div className="flex flex-col gap-3 sm:w-1/2">
            <div
              className="flex items-center gap-3 rounded-md border border-ws-border px-3 py-3"
              style={{ backgroundColor: color, transition: reduced ? undefined : 'background-color 320ms ease-out' }}
            >
              <div className="text-3xl font-bold tabular-nums" style={{ color: DARK_INK }}>
                {ph.toFixed(1)}
              </div>
              <div className="leading-tight" style={{ color: DARK_INK }}>
                <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80">indicator color</div>
                <div className="text-sm font-semibold">{nature}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Stat label="pH" value={ph.toFixed(2)} accent="var(--ws-accent)" />
              <Stat label="pOH" value={poh.toFixed(2)} />
              <Stat label="[H⁺] mol/L" value={sci(h)} />
              <Stat label="[OH⁻] mol/L" value={sci(oh)} />
            </div>

            <div className="flex items-center gap-2 rounded-md border border-ws-border bg-ws-surface-2 px-3 py-2">
              <span className="text-2xl leading-none">{near.emoji}</span>
              <span className="text-sm text-ws-ink-2">
                Closest to <span className="font-semibold text-ws-ink">{near.name}</span>{' '}
                <span className="text-ws-ink-3">(~pH {near.ph})</span>
              </span>
            </div>

            <p className="text-[11px] text-ws-ink-3">
              pH + pOH = 14 · [H⁺][OH⁻] = 1×10⁻¹⁴. Each step of 1 pH unit is a 10×
              change in [H⁺].
            </p>

            {/* Fine controls (also moves the marker; keeps keyboard users in play). */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => nudge(-0.5)}
                className="rounded-sm border border-ws-border bg-ws-tile px-3 py-1 text-sm font-medium text-ws-ink-2 transition-colors hover:bg-ws-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
                aria-label="Decrease pH"
              >
                − more acidic
              </button>
              <button
                type="button"
                onClick={() => nudge(0.5)}
                className="rounded-sm border border-ws-border bg-ws-tile px-3 py-1 text-sm font-medium text-ws-ink-2 transition-colors hover:bg-ws-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
                aria-label="Increase pH"
              >
                + more basic
              </button>
            </div>
          </div>
        </div>
      </div>
    </VizCard>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="rounded-md border border-ws-border bg-ws-tile px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-ws-ink-3">{label}</div>
      <div className="font-mono text-sm font-semibold tabular-nums" style={{ color: accent ?? 'var(--ws-ink)' }}>
        {value}
      </div>
    </div>
  );
}
